
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import authApiClient from '../authApiClient';

const GamificationContext = createContext();

export const useGamification = () => useContext(GamificationContext);

export const GamificationProvider = ({ children }) => {
    const [notification, setNotification] = useState(null); // { points, message, type }
    const levelRef = useRef(null);
    const pendingLevelCheckRef = useRef(null);

    const syncKnownLevel = useCallback((levelValue) => {
        const numericLevel = Number(levelValue);
        if (!Number.isFinite(numericLevel) || numericLevel <= 0) return null;
        levelRef.current = numericLevel;
        try {
            window.localStorage.setItem('fs_last_known_level', String(numericLevel));
        } catch (error) {
            // Ignore storage failures.
        }
        return numericLevel;
    }, []);

    useEffect(() => {
        try {
            const storedLevel = Number(window.localStorage.getItem('fs_last_known_level'));
            if (Number.isFinite(storedLevel) && storedLevel > 0) {
                levelRef.current = storedLevel;
            }
        } catch (error) {
            // Ignore storage failures.
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchInitialLevel = async () => {
            try {
                const response = await authApiClient.get('/gamification/state');
                if (!isMounted) return;
                syncKnownLevel(response?.data?.gamification?.level);
            } catch (error) {
                // Ignore auth/session errors here.
            }
        };

        fetchInitialLevel();

        return () => {
            isMounted = false;
        };
    }, [syncKnownLevel]);

    const showXpNotification = useCallback((points, message = 'Experience Gained!', options = {}) => {
        const timestamp = Date.now();
        const explicitLevel = Number(options?.newLevel || options?.gamification?.level);
        const previousLevel = Number(levelRef.current || 0);
        const inferredLevelUp = Boolean(
            options?.levelUp
            || (Number.isFinite(explicitLevel) && explicitLevel > previousLevel && previousLevel > 0)
        );

        if (Number.isFinite(explicitLevel) && explicitLevel > 0) {
            syncKnownLevel(explicitLevel);
        }

        setNotification({
            points,
            message,
            visible: true,
            timestamp,
            levelUp: inferredLevelUp,
            newLevel: Number.isFinite(explicitLevel) ? explicitLevel : null,
            celebrationPlayed: false
        });

        if (!Number.isFinite(explicitLevel) && pendingLevelCheckRef.current) {
            window.clearTimeout(pendingLevelCheckRef.current);
            pendingLevelCheckRef.current = null;
        }

        if (!Number.isFinite(explicitLevel)) {
            pendingLevelCheckRef.current = window.setTimeout(async () => {
                try {
                    const response = await authApiClient.get('/gamification/state');
                    const resolvedLevel = Number(response?.data?.gamification?.level || 0);
                    const knownLevel = Number(levelRef.current || 0);
                    if (Number.isFinite(resolvedLevel) && resolvedLevel > 0) {
                        syncKnownLevel(resolvedLevel);
                        if (resolvedLevel > knownLevel && knownLevel > 0) {
                            setNotification((prev) => (
                                prev && prev.timestamp === timestamp
                                    ? {
                                        ...prev,
                                        levelUp: true,
                                        newLevel: resolvedLevel
                                    }
                                    : prev
                            ));
                        }
                    }
                } catch (error) {
                    // Ignore level refresh failures.
                } finally {
                    pendingLevelCheckRef.current = null;
                }
            }, 250);
        }

        // Auto-hide after 7 seconds
        setTimeout(() => {
            setNotification(prev => (prev && prev.timestamp === timestamp ? null : prev));
        }, 7000);
    }, [syncKnownLevel]);

    const hideNotification = useCallback(() => {
        setNotification(null);
    }, []);

    return (
        <GamificationContext.Provider value={{ notification, showXpNotification, hideNotification, syncKnownLevel }}>
            {children}
        </GamificationContext.Provider>
    );
};
